import React, { FC, ReactNode } from 'react';
import API from 'core/Api';
import { getUserInfo } from 'utils/auth';
import MainLayout from 'components/main-layout/MainLayout';
import TabNavigation from 'components/TabNavigation';
import LayoutContent from 'components/main-layout/LayoutContent';
import { useTranslation } from 'react-i18next';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';
import { useHistory, useLocation } from 'react-router-dom';
import { useActiveClientContext } from 'store/active-client';
import { useQueryClient, useQuery } from 'react-query';
import styled from 'styled-components';

interface IProps {
  isLoading?: boolean;
  error?: unknown;
  children: React.ReactNode;
}

interface TabCount {
  receivedCount: number;
  forSignCount: number;
  sentCount: number;
}

interface Tab {
  to?: string;
  label?: string;
  entityName?: string;
  onClick?: () => void;
  onRefresh?: () => void;
  notification?: ReactNode;
}

const InnerWrapper: FC<IProps> = ({ isLoading, children, error }) => {
  const { t } = useTranslation();
  const [showCandidateAlert, setShowCandidateAlert] = React.useState(true);
  const location = useLocation();
  const history = useHistory();
  const { client } = useActiveClientContext();
  const queryClient = useQueryClient();
  const { isCandidate, employeeCandidateHelpText } = getUserInfo();

  const refreshDocuments = () => {
    const { pathname } = location;
    const currentTab = tabs.find(tab => tab.to === pathname);
    if (currentTab) {
      queryClient.resetQueries(currentTab.entityName);
      queryClient.invalidateQueries('tabCounts');
    }
  };

  // Fetch tab counts
  const { data: tabCounts } = useQuery<TabCount>('tabCounts', async () => {
    const response = await API.get('/document-holders/tab-counts');
    return response.data;
  });

  // Used for documents count in tabs
  const getTabNotification = (count?: number) => {
    if (count && count > 0) {
      const displayCount = count > 99 ? '99+' : count;
      return <NotificationBadge>{displayCount}</NotificationBadge>;
    }
    return null;
  };

  /**
   * !!!!!!!!!!!!!!!!!!!!!!!!
   *
   * If updating the tabs logic here, please make sure
   * that you update documents tabs as well
   *
   * !!!!!!!!!!!!!!!!!!!!!!!!
   */
  const tabs: Tab[] = [
    {
      to: '/my-documents/received',
      label: t('documents.received'),
      entityName: buildClientEntity('my-documents-received', client),
      notification: getTabNotification(tabCounts?.receivedCount),
    },
    {
      to: '/my-documents/ready-for-sign-send',
      label: t('documents.awaiting'),
      entityName: buildClientEntity('my-documents-for-sign', client),
      notification: getTabNotification(tabCounts?.forSignCount),
    },
    {
      to: '/my-documents/sent',
      label: t('documents.sent'),
      entityName: buildClientEntity('my-documents-sent', client),
      notification: getTabNotification(tabCounts?.sentCount),
    },
    {
      to: '/my-documents/archived',
      label: t('documents.file'),
      entityName: buildClientEntity('my-documents-archived', client),
    },
    {
      to: '/my-documents/trash',
      label: t('documents.trash'),
      entityName: buildClientEntity('my-documents-trash', client),
    },
    {
      onRefresh: refreshDocuments,
    },
  ].map(tab => {
    if (tab.entityName)
      return {
        ...tab,
        onClick: () => {
          queryClient.resetQueries(tab.entityName);
          queryClient.invalidateQueries('tabCounts');
          history.push(buildClientUrl(tab.to, client));
        },
      };

    return tab;
  });

  return (
    <MainLayout title={t('documents.title')}>
      {isCandidate && showCandidateAlert && (
        <div
          className="alert alert-success position-relative"
          role="note"
        >
          <button
            type="button"
            className="position-absolute"
            style={{
              top: 12,
              right: 16,
              zIndex: 2,
              background: 'none',
              border: 'none',
              fontSize: 22,
              lineHeight: 1,
              cursor: 'pointer',
            }}
            aria-label="Close"
            onClick={() => setShowCandidateAlert(false)}
          >
            &#10006;
          </button>
          <div dangerouslySetInnerHTML={{ __html: employeeCandidateHelpText }} />
        </div>
      )}
      <TabNavigation tabs={tabs} />
      <LayoutContent
        isLoading={isLoading}
        error={error}
      >
        {children}
      </LayoutContent>
    </MainLayout>
  );
};

export default InnerWrapper;

const NotificationBadge = styled.span`
  background-color: var(--primary);
  color: white;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  font-size: 0.8em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -12px;
  right: -20px;
  white-space: nowrap;

  @media ${props => props.theme.breakpoints.sm} {
    top: -10px;
    right: -15px;
    width: 20px;
    height: 20px;
    font-size: 0.7em;
  }
`;
